import { Component, Suspense, lazy, useEffect, useRef, useState, type ErrorInfo, type ReactNode } from 'react';
import { CAREER_GUIDANCE_NOTICE } from '@worksim/engine';
import { StoreProvider, useStore } from './state/store';
import { href, navigate, useRoute, type Route } from './router';
import { ErrorNotice, Loading, SignInPrompt } from './components/common';
import { Avatar, Icon, ThemeToggle, ToastProvider, type IconName } from './components/ui';
import { workplace } from './api/client';
import { useAsync } from './hooks/useAsync';
import { LandingPage } from './pages/home/LandingPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailNotice, VerifyEmailPage } from './pages/EmailPages';

// Route-level code splitting: the landing page, sign-in and dashboard load with the shell; every other page is fetched on first visit.
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })));
const CareerMapPage = lazy(() => import('./pages/CareerMapPage').then((m) => ({ default: m.CareerMapPage })));
const MentorPage = lazy(() => import('./pages/MentorPage').then((m) => ({ default: m.MentorPage })));
const CoachPage = lazy(() => import('./pages/coach/CoachPage').then((m) => ({ default: m.CoachPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const AssignmentsPage = lazy(() => import('./pages/AssignmentsPage').then((m) => ({ default: m.AssignmentsPage })));
const ExplorePage = lazy(() => import('./pages/ExplorePage').then((m) => ({ default: m.ExplorePage })));
const CareerPage = lazy(() => import('./pages/CareerPage').then((m) => ({ default: m.CareerPage })));
const ComparePage = lazy(() => import('./pages/ComparePage').then((m) => ({ default: m.ComparePage })));
const WorkplacePage = lazy(() => import('./pages/workplace/WorkplacePage').then((m) => ({ default: m.WorkplacePage })));
const ScenarioPage = lazy(() => import('./pages/scenario/ScenarioPage').then((m) => ({ default: m.ScenarioPage })));
const ProgressPage = lazy(() => import('./pages/ProgressPage').then((m) => ({ default: m.ProgressPage })));
const LearnHub = lazy(() => import('./pages/learn/LearnHub').then((m) => ({ default: m.LearnHub })));
const PathPage = lazy(() => import('./pages/learn/PathPage').then((m) => ({ default: m.PathPage })));
const LessonPage = lazy(() => import('./pages/learn/LessonPage').then((m) => ({ default: m.LessonPage })));
const LabPage = lazy(() => import('./pages/learn/LabPage').then((m) => ({ default: m.LabPage })));
const LabSessionPage = lazy(() => import('./pages/learn/LabSessionPage').then((m) => ({ default: m.LabSessionPage })));
const CertificationPage = lazy(() => import('./pages/learn/CertificationsPage').then((m) => ({ default: m.CertificationPage })));
const CertificationsPage = lazy(() => import('./pages/learn/CertificationsPage').then((m) => ({ default: m.CertificationsPage })));
const InterviewHub = lazy(() => import('./pages/interview/InterviewHub').then((m) => ({ default: m.InterviewHub })));
const KitPage = lazy(() => import('./pages/interview/KitPage').then((m) => ({ default: m.KitPage })));
const InterviewSessionPage = lazy(() => import('./pages/interview/SessionPage').then((m) => ({ default: m.SessionPage })));
const DeliverablesPage = lazy(() => import('./pages/DeliverablesPage').then((m) => ({ default: m.DeliverablesPage })));
const ProgramPage = lazy(() => import('./pages/ProgramPage').then((m) => ({ default: m.ProgramPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const ProjectsPage = lazy(() => import('./pages/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })));
const ProjectPage = lazy(() => import('./pages/projects/ProjectPage').then((m) => ({ default: m.ProjectPage })));
const ProjectSessionPage = lazy(() => import('./pages/projects/ProjectSessionPage').then((m) => ({ default: m.ProjectSessionPage })));
const ResumePage = lazy(() => import('./pages/portfolio/ResumePage').then((m) => ({ default: m.ResumePage })));
const PrepHub = lazy(() => import('./pages/prep/PrepHub').then((m) => ({ default: m.PrepHub })));
const BankPage = lazy(() => import('./pages/prep/BankPage').then((m) => ({ default: m.BankPage })));
const PracticeSessionPage = lazy(() => import('./pages/prep/PracticeSessionPage').then((m) => ({ default: m.PracticeSessionPage })));
const FlashcardsPage = lazy(() => import('./pages/prep/FlashcardsPage').then((m) => ({ default: m.FlashcardsPage })));
const AgentPage = lazy(() => import('./pages/agents/AgentPage').then((m) => ({ default: m.AgentPage })));
const ResearchPage = lazy(() => import('./pages/agents/ResearchPage').then((m) => ({ default: m.ResearchPage })));
const ReviewersPage = lazy(() => import('./pages/reviewers/ReviewersPage').then((m) => ({ default: m.ReviewersPage })));
const ReviewAccessPage = lazy(() => import('./pages/reviewers/ReviewAccessPage').then((m) => ({ default: m.ReviewAccessPage })));
const AdminPage = lazy(() => import('./pages/admin/AdminPage').then((m) => ({ default: m.AdminPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const SkillsHub = lazy(() => import('./pages/skills/SkillsHub').then((m) => ({ default: m.SkillsHub })));
const SkillPage = lazy(() => import('./pages/skills/SkillPage').then((m) => ({ default: m.SkillPage })));
const SkillMapPage = lazy(() => import('./pages/skills/SkillMapPage').then((m) => ({ default: m.SkillMapPage })));
const FoundationsPage = lazy(() => import('./pages/skills/FoundationsPage').then((m) => ({ default: m.FoundationsPage })));
const AnalyticsPage = lazy(() => import('./pages/skills/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const KnowledgeHub = lazy(() => import('./pages/knowledge/KnowledgeHub').then((m) => ({ default: m.KnowledgeHub })));
const TopicPage = lazy(() => import('./pages/knowledge/TopicPage').then((m) => ({ default: m.TopicPage })));
const CheatSheetsPage = lazy(() => import('./pages/cheatsheets/CheatSheetsPage').then((m) => ({ default: m.CheatSheetsPage })));
const CheatSheetPage = lazy(() => import('./pages/cheatsheets/CheatSheetPage').then((m) => ({ default: m.CheatSheetPage })));
const GuidesHub = lazy(() => import('./pages/guides/GuidesHub').then((m) => ({ default: m.GuidesHub })));
const GuidePage = lazy(() => import('./pages/guides/GuidePage').then((m) => ({ default: m.GuidePage })));
const ResourcesPage = lazy(() => import('./pages/resources/ResourcesPage').then((m) => ({ default: m.ResourcesPage })));
const AssessmentsPage = lazy(() => import('./pages/assessments/AssessmentsPage').then((m) => ({ default: m.AssessmentsPage })));
const AssessmentPage = lazy(() => import('./pages/assessments/AssessmentsPage').then((m) => ({ default: m.AssessmentPage })));
const AssessmentSessionPage = lazy(() => import('./pages/assessments/AssessmentSessionPage').then((m) => ({ default: m.AssessmentSessionPage })));
const MyLabPage = lazy(() => import('./pages/workspace/MyLabPage').then((m) => ({ default: m.MyLabPage })));
const CodeLabsPage = lazy(() => import('./pages/codelabs/CodeLabsPage').then((m) => ({ default: m.CodeLabsPage })));
const CodeLabPage = lazy(() => import('./pages/codelabs/CodeLabPage').then((m) => ({ default: m.CodeLabPage })));
const CodeLabSessionPage = lazy(() => import('./pages/codelabs/CodeLabSessionPage').then((m) => ({ default: m.CodeLabSessionPage })));
const VmLabsPage = lazy(() => import('./pages/vmlabs/VmLabsPage').then((m) => ({ default: m.VmLabsPage })));
const VmLabPage = lazy(() => import('./pages/vmlabs/VmLabPage').then((m) => ({ default: m.VmLabPage })));
const VmSessionPage = lazy(() => import('./pages/vmlabs/VmSessionPage').then((m) => ({ default: m.VmSessionPage })));

interface NavItem { label: string; to: string; icon: IconName; active: boolean; auth?: boolean; admin?: boolean; count?: number }

const PUBLIC_PAGES = new Set(['home', 'explore', 'career', 'career-map', 'compare', 'about', 'login', 'register', 'forgot', 'reset', 'verify', 'learn', 'learn-lesson', 'learn-lab', 'certifications', 'certification', 'interview', 'interview-kit', 'projects', 'project', 'research']);
// The skill catalog, skill pages, skill maps and the foundations pathway are public; mastery appears when signed in.
for (const p of ['skills', 'skill', 'skill-map', 'foundations']) PUBLIC_PAGES.add(p);
// Essential knowledge, cheat sheets, the resource library and the assessment catalog are public; checks, favorites and attempts need an account.
for (const p of ['knowledge', 'knowledge-topic', 'cheatsheets', 'cheatsheet', 'resources', 'assessments', 'assessment']) PUBLIC_PAGES.add(p);
// Skill guides are public to read; checks and mastery challenges need an account.
for (const p of ['guides', 'guide']) PUBLIC_PAGES.add(p);
/** Pages that manage their own width (marketing pages and immersive players). */
const FULL_BLEED = new Set(['home']);
const WIDE = new Set(['scenario', 'learn-lab-session', 'workplace', 'dashboard', 'project-session', 'prep-session', 'progress']);
// Code lab and VM lab catalogs and lab pages are public; sessions need an account. Their consoles use the wide layout.
for (const p of ['codelabs', 'codelab', 'vmlabs', 'vmlab']) PUBLIC_PAGES.add(p);
for (const p of ['codelab-session', 'vm-session']) WIDE.add(p);

function Shell() {
  const route = useRoute();
  const { status, user, error, logout } = useStore();
  const isAuthed = status === 'ready' && Boolean(user);
  const unread = useAsync(async () => (user ? (await workplace.overview()).workplace.unreadNotifications : 0), [user?.id, route.page]);
  const unreadCount = unread.data ?? 0;

  const page = route.page;
  const active = (...pages: string[]) => pages.some((p) => page === p || page.startsWith(`${p}-`));
  const primary: NavItem[] = user
    ? [
        { label: 'Dashboard', to: href({ page: 'dashboard' }), icon: 'home', active: page === 'dashboard' },
        { label: 'Coach', to: href({ page: 'coach' }), icon: 'compass-star', active: page === 'coach' },
        { label: 'Career tracks', to: href({ page: 'explore' }), icon: 'compass', active: active('explore', 'career', 'compare') || page === 'career-map' },
        { label: 'Learn', to: href({ page: 'learn' }), icon: 'book', active: active('learn', 'certifications', 'certification') },
        { label: 'Skills', to: href({ page: 'skills' }), icon: 'graph', active: active('skill') || page === 'skills' || page === 'foundations' },
        { label: 'Knowledge', to: href({ page: 'knowledge' }), icon: 'lightbulb', active: active('knowledge') },
        { label: 'Skill guides', to: href({ page: 'guides' }), icon: 'list', active: page === 'guides' || page === 'guide' || page === 'cheatsheets' || page === 'cheatsheet' },
        { label: 'Code labs', to: href({ page: 'codelabs' }), icon: 'code', active: active('codelab') || page === 'codelabs' },
        { label: 'VM labs', to: href({ page: 'vmlabs' }), icon: 'server', active: page === 'vmlabs' || page === 'vmlab' || page === 'vm-session' },
        { label: 'My Lab', to: href({ page: 'lab-workspace' }), icon: 'flask', active: page === 'lab-workspace' },
        { label: 'Workplace', to: href({ page: 'workplace' }), icon: 'briefcase', active: page === 'workplace' || page === 'scenario' },
        { label: 'Projects', to: href({ page: 'projects' }), icon: 'flag', active: active('project') || page === 'projects' },
        { label: 'Certification prep', to: href({ page: 'prep' }), icon: 'star', active: active('prep') },
        { label: 'Assignments', to: href({ page: 'assignments' }), icon: 'clipboard', active: page === 'assignments' },
        { label: 'Progress', to: href({ page: 'progress' }), icon: 'chart', active: page === 'progress' || page === 'analytics' },
        { label: 'Portfolio', to: href({ page: 'deliverables' }), icon: 'folder', active: page === 'deliverables' || page === 'resume' },
        { label: 'AI Mentor', to: href({ page: 'mentor' }), icon: 'sparkles', active: page === 'mentor' },
        { label: 'Career agent', to: href({ page: 'agent' }), icon: 'chat', active: page === 'agent' || page === 'research' },
        { label: 'Reviewers', to: href({ page: 'reviewers' }), icon: 'users', active: page === 'reviewers' || page === 'review' },
        { label: 'Interview prep', to: href({ page: 'interview' }), icon: 'target', active: active('interview') },
        { label: 'Program', to: href({ page: 'program' }), icon: 'calendar', active: page === 'program' },
        { label: 'Notifications', to: href({ page: 'notifications' }), icon: 'bell', active: page === 'notifications', count: unreadCount },
      ]
    : [
        { label: 'Home', to: href({ page: 'home' }), icon: 'home', active: page === 'home' },
        { label: 'Career tracks', to: href({ page: 'explore' }), icon: 'compass', active: active('explore', 'career', 'compare') || page === 'career-map' },
        { label: 'Learn', to: href({ page: 'learn' }), icon: 'book', active: active('learn', 'certifications', 'certification') },
        { label: 'Knowledge', to: href({ page: 'knowledge' }), icon: 'lightbulb', active: active('knowledge') },
        { label: 'Skill guides', to: href({ page: 'guides' }), icon: 'list', active: page === 'guides' || page === 'guide' || page === 'cheatsheets' || page === 'cheatsheet' },
        { label: 'Code labs', to: href({ page: 'codelabs' }), icon: 'code', active: active('codelab') || page === 'codelabs' },
        { label: 'VM labs', to: href({ page: 'vmlabs' }), icon: 'server', active: page === 'vmlabs' || page === 'vmlab' || page === 'vm-session' },
        { label: 'Interview prep', to: href({ page: 'interview' }), icon: 'target', active: active('interview') },
        { label: 'About', to: href({ page: 'about' }), icon: 'info', active: page === 'about' },
      ];
  const adminItem: NavItem | undefined = user?.role === 'admin' ? { label: 'Admin', to: href({ page: 'admin' }), icon: 'settings', active: page === 'admin', admin: true } : undefined;
  const needsUser = !PUBLIC_PAGES.has(page);
  const showSidebar = isAuthed;

  const onLogout = async () => {
    await logout();
    navigate({ page: 'home' });
  };

  // Signed-in visitors landing on the marketing page go straight to their dashboard.
  useEffect(() => {
    if (page === 'home' && isAuthed) navigate({ page: 'dashboard' });
  }, [page, isAuthed]);

  const mainClass = FULL_BLEED.has(page) ? 'full-bleed' : WIDE.has(page) ? 'wide' : '';

  return (
    <div className={`app ${showSidebar ? 'with-sidebar' : 'no-sidebar'}`}>
      <a className="skip-link" href="#main">Skip to main content</a>
      <TopBar route={route} user={user} status={status} primary={primary} adminItem={adminItem} unread={unreadCount} onLogout={() => void onLogout()} />
      {showSidebar && (
        <aside className="sidebar desktop" aria-label="Primary">
          <SidebarNav primary={primary} adminItem={adminItem} />
        </aside>
      )}
      <main id="main" tabIndex={-1} className={mainClass}>
        {isAuthed && page !== 'verify' && <VerifyEmailNotice />}
        {error && <ErrorNotice message={error} />}
        {status === 'loading' && needsUser ? (
          <Loading what="Checking your sign-in" />
        ) : needsUser && !user ? (
          <SignInPrompt what={pageName(page)} page />
        ) : (
          <PageBoundary key={window.location.hash}>
            <Suspense fallback={<Loading what="Loading this page" />}>
              <PageSwitch route={route} />
            </Suspense>
          </PageBoundary>
        )}
      </main>
      <footer className="site-footer">
        <div className="inner">
          <p style={{ margin: 0, maxWidth: '70ch' }}>{CAREER_GUIDANCE_NOTICE}</p>
          <nav aria-label="Footer">
            <a href={href({ page: 'about' })}>About</a>
            <a href={href({ page: 'explore' })}>Career tracks</a>
            <a href={href({ page: 'learn' })}>Learn</a>
          </nav>
        </div>
      </footer>
      {isAuthed && <BottomNav primary={primary} />}
    </div>
  );
}

function TopBar({ route, user, status, primary, adminItem, unread, onLogout }: { route: Route; user: ReturnType<typeof useStore>['user']; status: string; primary: NavItem[]; adminItem?: NavItem; unread: number; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const drawer = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = drawer.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  useEffect(() => setOpen(false), [route]);
  const topLinks = user ? [] : primary;
  return (
    <header className="topbar">
      <button type="button" className="ghost icon-button menu-button" aria-label="Open navigation" aria-expanded={open} onClick={() => setOpen(true)}>
        <Icon name="menu" />
      </button>
      <a className="brand" href={href({ page: user ? 'dashboard' : 'home' })} aria-label="WorkSim AI home">
        <span className="brand-mark" aria-hidden="true">WS</span>
        <span>
          <span className="brand-name">WorkSim AI</span>
          <span className="brand-sub">TechCore Solutions career simulator</span>
        </span>
      </a>
      {topLinks.length > 0 && (
        <nav aria-label="Primary">
          <ul className="top-links">
            {topLinks.map((n) => (
              <li key={n.to}><a href={n.to} aria-current={n.active ? 'page' : undefined}>{n.label}</a></li>
            ))}
          </ul>
        </nav>
      )}
      <span className="spacer" />
      <div className="account">
        <ThemeToggle />
        {status === 'loading' && <span className="small muted">Checking sign-in…</span>}
        {status === 'ready' && !user && (
          <>
            <a className="button quiet small login-link" href={href({ page: 'login' })} aria-current={route.page === 'login' ? 'page' : undefined}>Log in</a>
            <a className="button small" href={href({ page: 'register' })} aria-current={route.page === 'register' ? 'page' : undefined}>Get started</a>
          </>
        )}
        {status === 'ready' && user && (
          <>
            <a className="ghost icon-button bell button" href={href({ page: 'notifications' })} aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'} title="Notifications">
              <Icon name="bell" />
              {unread > 0 && <span className="count" aria-hidden="true">{unread > 9 ? '9+' : unread}</span>}
            </a>
            <a className="profile-link" href={href({ page: 'profile' })} aria-current={route.page === 'profile' ? 'page' : undefined} title="Your profile">
              <Avatar name={user.displayName} size="sm" />
              <span className="hide-mobile">{user.displayName}</span>
            </a>
            <button type="button" className="quiet small logout" onClick={onLogout}>Log out</button>
          </>
        )}
      </div>
      <dialog ref={drawer} className="drawer" aria-label="Navigation" onClose={() => setOpen(false)}>
        <div className="drawer-head">
          <span className="brand-name">Menu</span>
          <button type="button" className="ghost icon-button" aria-label="Close navigation" onClick={() => setOpen(false)}><Icon name="close" /></button>
        </div>
        <div className="sidebar">
          <SidebarNav primary={primary} adminItem={adminItem} />
          {user && (
            <div className="actions" style={{ margin: 0 }}>
              <a className="button quiet small" href={href({ page: 'profile' })}>Your profile</a>
              <button type="button" className="quiet small" onClick={onLogout}>Log out</button>
            </div>
          )}
        </div>
      </dialog>
    </header>
  );
}

function SidebarNav({ primary, adminItem }: { primary: NavItem[]; adminItem?: NavItem }) {
  const link = (n: NavItem) => (
    <li key={n.to}>
      <a className="side-link" href={n.to} aria-current={n.active ? 'page' : undefined}>
        <Icon name={n.icon} />
        <span>{n.label}</span>
        {n.count ? <span className="count" aria-label={`${n.count} unread`}>{n.count > 9 ? '9+' : n.count}</span> : null}
      </a>
    </li>
  );
  const MORE = ['Interview prep', 'Program', 'Notifications', 'Reviewers', 'Assignments'];
  const main = primary.filter((n) => !MORE.includes(n.label));
  const more = primary.filter((n) => MORE.includes(n.label));
  return (
    <>
      <nav aria-label="Main">
        <ul>{main.map(link)}</ul>
      </nav>
      {more.length > 0 && (
        <nav aria-label="More">
          <div className="group-label">More</div>
          <ul>{more.map(link)}{adminItem && link(adminItem)}</ul>
        </nav>
      )}
      <div className="side-card">
        <strong>TechCore Solutions</strong>
        <span className="muted">Your virtual employer. Every scenario, ticket and colleague lives here.</span>
      </div>
    </>
  );
}

function BottomNav({ primary }: { primary: NavItem[] }) {
  const picks = ['Dashboard', 'Career tracks', 'Workplace', 'Learn', 'Progress'];
  const items = picks.map((p) => primary.find((n) => n.label === p)).filter((n): n is NavItem => Boolean(n));
  return (
    <nav className="bottom-nav" aria-label="Quick navigation">
      {items.map((n) => (
        <a key={n.to} href={n.to} aria-current={n.active ? 'page' : undefined}>
          <Icon name={n.icon} />
          <span>{n.label === 'Career tracks' ? 'Careers' : n.label}</span>
        </a>
      ))}
    </nav>
  );
}

/** Keeps the shell on screen when a page throws while rendering, and offers a way back. */
class PageBoundary extends Component<{ children: ReactNode }, { error?: Error }> {
  override state: { error?: Error } = {};
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Page failed to render', error, info.componentStack);
  }
  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="page-enter">
        <h1>This page hit a problem</h1>
        <p className="notice bad" role="alert">Something went wrong while drawing this page. Nothing you submitted has been lost; your work is saved on the server as you go.</p>
        <p className="small muted">{this.state.error.message}</p>
        <div className="actions">
          <button type="button" onClick={() => this.setState({ error: undefined })}>Try again</button>
          <a className="button secondary" href={href({ page: 'dashboard' })}>Go to your dashboard</a>
        </div>
      </div>
    );
  }
}

function PageSwitch({ route }: { route: Route }): ReactNode {
  switch (route.page) {
    case 'home': return <LandingPage />;
    case 'dashboard': return <DashboardPage />;
    case 'onboarding': return <OnboardingPage step={route.step} />;
    case 'career-map': return <CareerMapPage focus={route.focus} />;
    case 'mentor': return <MentorPage />;
    case 'coach': return <CoachPage tab={route.tab} q={route.q} />;
    case 'notifications': return <NotificationsPage />;
    case 'assignments': return <AssignmentsPage />;
    case 'explore': return <ExplorePage />;
    case 'career': return <CareerPage id={route.id} tab={route.tab} />;
    case 'compare': return <ComparePage a={route.a} b={route.b} />;
    case 'workplace': return <WorkplacePage tab={route.tab} id={route.id} />;
    case 'scenario': return <ScenarioPage id={route.id} careerId={route.career} sessionId={route.session} />;
    case 'learn': return <LearnHub />;
    case 'learn-path': return <PathPage id={route.id} />;
    case 'learn-lesson': return <LessonPage id={route.id} />;
    case 'learn-lab': return <LabPage id={route.id} />;
    case 'learn-lab-session': return <LabSessionPage id={route.id} />;
    case 'certifications': return <CertificationsPage />;
    case 'certification': return <CertificationPage id={route.id} />;
    case 'interview': return <InterviewHub career={route.career} />;
    case 'interview-kit': return <KitPage careerId={route.careerId} />;
    case 'interview-session': return <InterviewSessionPage id={route.id} focusQuestion={route.q} />;
    case 'progress': return <ProgressPage tab={route.tab} />;
    case 'projects': return <ProjectsPage career={route.career} />;
    case 'project': return <ProjectPage id={route.id} />;
    case 'project-session': return <ProjectSessionPage id={route.id} />;
    case 'resume': return <ResumePage />;
    case 'prep': return <PrepHub />;
    case 'prep-bank': return <BankPage id={route.id} />;
    case 'prep-session': return <PracticeSessionPage id={route.id} />;
    case 'prep-flashcards': return <FlashcardsPage id={route.id} />;
    case 'agent': return <AgentPage career={route.career} />;
    case 'research': return <ResearchPage career={route.career} />;
    case 'reviewers': return <ReviewersPage />;
    case 'review': return <ReviewAccessPage grantId={route.grantId} view={route.view} />;
    case 'deliverables': return <DeliverablesPage id={route.id} />;
    case 'program': return <ProgramPage />;
    case 'profile': return <ProfilePage />;
    case 'login': return <LoginPage />;
    case 'register': return <RegisterPage />;
    case 'forgot': return <ForgotPasswordPage />;
    case 'reset': return <ResetPasswordPage token={route.token} />;
    case 'verify': return <VerifyEmailPage token={route.token} />;
    case 'admin': return <AdminPage section={route.section} id={route.id} type={route.type} />;
    case 'skills': return <SkillsHub />;
    case 'skill': return <SkillPage id={route.id} />;
    case 'skill-map': return <SkillMapPage career={route.career} />;
    case 'foundations': return <FoundationsPage />;
    case 'analytics': return <AnalyticsPage />;
    case 'knowledge': return <KnowledgeHub />;
    case 'knowledge-topic': return <TopicPage id={route.id} />;
    case 'cheatsheets': return <CheatSheetsPage q={route.q} category={route.category} />;
    case 'cheatsheet': return <CheatSheetPage id={route.id} />;
    case 'guides': return <GuidesHub q={route.q} domain={route.domain} />;
    case 'guide': return <GuidePage id={route.id} level={route.level} section={route.section} />;
    case 'resources': return <ResourcesPage skill={route.skill} career={route.career} kind={route.kind} q={route.q} />;
    case 'assessments': return <AssessmentsPage />;
    case 'assessment': return <AssessmentPage id={route.id} />;
    case 'assessment-session': return <AssessmentSessionPage id={route.id} />;
    case 'lab-workspace': return <MyLabPage />;
    case 'codelabs': return <CodeLabsPage career={route.career} language={route.language} />;
    case 'codelab': return <CodeLabPage id={route.id} />;
    case 'codelab-session': return <CodeLabSessionPage id={route.id} />;
    case 'vmlabs': return <VmLabsPage career={route.career} />;
    case 'vmlab': return <VmLabPage id={route.id} />;
    case 'vm-session': return <VmSessionPage id={route.id} />;
    case 'about': return <AboutPage />;
  }
}

function pageName(page: string): string {
  switch (page) {
    case 'dashboard': return 'your dashboard';
    case 'onboarding': return 'onboarding';
    case 'workplace': return 'the workplace';
    case 'assignments': return 'your assignments';
    case 'notifications': return 'your notifications';
    case 'mentor': return 'the AI mentor';
    case 'coach': return 'the Learning Coach';
    case 'scenario': return 'scenarios';
    case 'progress': return 'your progress';
    case 'deliverables': return 'your portfolio';
    case 'resume': return 'your resume evidence';
    case 'projects': case 'project': case 'project-session': return 'projects';
    case 'prep': case 'prep-bank': case 'prep-session': case 'prep-flashcards': return 'certification prep';
    case 'agent': return 'your career agent';
    case 'research': return 'research briefs';
    case 'reviewers': case 'review': return 'reviewer access';
    case 'program': return 'the 30-day program';
    case 'learn-path': return 'your learning path';
    case 'learn-lab-session': return 'labs';
    case 'interview-session': return 'interview practice';
    case 'profile': return 'your profile';
    case 'admin': return 'content administration';
    case 'analytics': return 'your learning analytics';
    case 'assessment-session': return 'assessments';
    case 'lab-workspace': return 'My Lab';
    case 'codelab-session': return 'code labs';
    case 'vm-session': return 'VM labs';
    default: return 'this page';
  }
}

export function App() {
  return (
    <StoreProvider>
      <ToastProvider>
        <Shell />
      </ToastProvider>
    </StoreProvider>
  );
}
